import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Icon,
  alpha,
  useTheme,
  Button,
  Snackbar,
} from "@mui/material";
import moment from "moment";
import { JournalSettings } from "../../../mock.data";
import { useConfiguration } from "../../../components/ConfigurationContext";

const availableRoles = ["Admin", "Finance Manager", "Accountant", "Data Entry"];
const availableCurrencies = ["BHD", "USD", "EUR", "GBP", "SAR", "AED"];

export const JournalSettingsConfig: React.FC = () => {
  const theme = useTheme();
  const { journalSettings, updateJournalSettings } = useConfiguration();
  const [settings, setSettings] = useState<JournalSettings>(journalSettings);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const handleToggleChange = (field: keyof JournalSettings) => {
    setSettings((prev) => ({
      ...prev,
      [field]: !prev[field],
      updated_at: moment().valueOf(),
    }));
  };

  const handleTextChange = (field: keyof JournalSettings, value: string) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
      updated_at: moment().valueOf(),
    }));
  };

  const handleRoleChange = (field: keyof JournalSettings, roles: string[]) => {
    setSettings((prev) => ({
      ...prev,
      [field]: roles,
      updated_at: moment().valueOf(),
    }));
  };

  const handleSaveSettings = () => {
    console.log("Saving journal settings:", settings);
    updateJournalSettings(settings);
    // Simulate API call
    setTimeout(() => {
      setSnackbar({
        open: true,
        message: "Journal settings saved successfully!",
        severity: "success",
      });
    }, 500);
  };

  const handleResetToDefaults = () => {
    setSettings(journalSettings);
    setSnackbar({
      open: true,
      message: "Settings reset to defaults",
      severity: "success",
    });
  };

  const getComplianceStatus = () => {
    const criticalSettings = [
      settings.enable_full_audit_trail,
      settings.auto_lock_on_post,
      settings.restrict_partial_entries,
      settings.prevent_duplicate_entries,
    ];
    const compliantCount = criticalSettings.filter(Boolean).length;
    return {
      level:
        compliantCount === 4 ? "high" : compliantCount >= 2 ? "medium" : "low",
      score: compliantCount,
      total: 4,
    };
  };

  const compliance = getComplianceStatus();

  const workflowRules = [
    {
      key: "enable_draft_journals",
      label: "Enable Draft Journals",
      description: "Allow saving entries as drafts without posting",
      color: "primary",
    },
    {
      key: "allow_manual_journals",
      label: "Allow Manual Journals",
      description: "Enable manual journal entry screen for authorized users",
      color: "primary",
    },
    {
      key: "allow_edits_after_save",
      label: "Allow Edits After Save",
      description: "If disabled, journals become immutable after save",
      color: "warning",
    },
    {
      key: "auto_lock_on_post",
      label: "Auto-lock on Post",
      description: "Once posted, journal cannot be edited or deleted",
      color: "error",
    },
    {
      key: "allow_reversal_journals",
      label: "Allow Reversal Journals",
      description: "Enable system to generate reversal (mirror) entries",
      color: "warning",
    },
    {
      key: "restrict_partial_entries",
      label: "Restrict Partial Entries",
      description: "Prevent saving unless total debit = credit",
      color: "success",
    },
    {
      key: "restrict_save_without_narration",
      label: "Restrict Save Without Narration",
      description: "Force user to enter a reason/narration",
      color: "info",
    },
  ];

  const auditSettings = [
    {
      key: "enable_full_audit_trail",
      label: "Enable Full Audit Trail",
      description:
        "Log all create/post/reverse actions with user ID, IP, timestamp",
      color: "success",
    },
    {
      key: "store_original_entry_snapshot",
      label: "Store Original Entry Snapshot",
      description: "Store JSON copy of pre-edit version (if edits allowed)",
      color: "info",
    },
    {
      key: "log_coa_account_hits",
      label: "Log COA Account Hits",
      description: "Log which accounts were impacted by which journal",
      color: "primary",
    },
    {
      key: "audit_trail_logging",
      label: "General Audit Trail Logging",
      description: "Enable basic audit trail functionality",
      color: "success",
    },
  ];

  const validationRules = [
    {
      key: "enforce_coa_postability",
      label: "Enforce COA Postability",
      description: "Prevent use of header (non-postable) COA accounts",
      color: "error",
    },
    {
      key: "enforce_account_type_match",
      label: "Enforce Account Type Match",
      description:
        "Prevent incorrect pairings (e.g., Revenue → Asset mismatch)",
      color: "warning",
    },
    {
      key: "allow_zero_amount_lines",
      label: "Allow Zero Amount Lines",
      description: "Enable 0 BHD rows for documentation/reference only",
      color: "info",
    },
    {
      key: "prevent_duplicate_entries",
      label: "Prevent Duplicate Entries",
      description:
        "Hash check on posted journal to prevent accidental reposting",
      color: "success",
    },
  ];

  const advancedFeatures = [
    {
      key: "enable_cost_center_tracking",
      label: "Enable Cost Center Tracking",
      description: "Associate each journal with department or project",
      color: "primary",
    },
    {
      key: "enable_departmental_segmenting",
      label: "Enable Departmental Segmenting",
      description: "Allow line-level classification by department",
      color: "primary",
    },
    {
      key: "enable_subledger_source_tagging",
      label: "Enable Subledger Source Tagging",
      description: "e.g., 'Source: POS', 'Source: Payroll', 'Source: Manual'",
      color: "info",
    },
  ];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          p: 3,
          bgcolor: "background.paper",
          borderRadius: 0.5,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}`,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
          >
            Journal Settings Configuration
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", fontSize: "1rem" }}
          >
            Configure journal entry workflow, validation rules, and compliance
            settings
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" onClick={handleResetToDefaults}>
            Reset to Defaults
          </Button>
          <Button variant="contained" onClick={handleSaveSettings}>
            Save Settings
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: 1, p: 3 }}>
        <Grid container spacing={4}>
          {/* Compliance Status Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                border: `2px solid ${
                  compliance.level === "high"
                    ? theme.palette.success.main
                    : compliance.level === "medium"
                    ? theme.palette.warning.main
                    : theme.palette.error.main
                }`,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Icon
                      sx={{
                        fontSize: 32,
                        color:
                          compliance.level === "high"
                            ? "success.main"
                            : compliance.level === "medium"
                            ? "warning.main"
                            : "error.main",
                      }}
                    >
                      {compliance.level === "high"
                        ? "verified"
                        : compliance.level === "medium"
                        ? "warning"
                        : "error"}
                    </Icon>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Compliance Status: {compliance.level.toUpperCase()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {compliance.score}/{compliance.total} critical settings
                        enabled
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={`${Math.round(
                      (compliance.score / compliance.total) * 100
                    )}% Compliant`}
                    color={
                      compliance.level === "high"
                        ? "success"
                        : compliance.level === "medium"
                        ? "warning"
                        : "error"
                    }
                    variant="filled"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Section 1: Journal Entry Workflow Rules */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "primary.main" }}>settings</Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 1: Journal Entry Workflow Rules
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      This defines what is allowed in the user journey of
                      journal entry creation
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  {workflowRules.map((rule) => (
                    <Grid item xs={12} sm={6} md={4} key={rule.key}>
                      <Card variant="outlined" sx={{ height: "100%" }}>
                        <CardContent>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  settings[
                                    rule.key as keyof JournalSettings
                                  ] as boolean
                                }
                                onChange={() =>
                                  handleToggleChange(
                                    rule.key as keyof JournalSettings
                                  )
                                }
                                color={rule.color as any}
                              />
                            }
                            label={
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {rule.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                  }}
                                >
                                  {rule.description}
                                </Typography>
                              </Box>
                            }
                            sx={{ alignItems: "flex-start", m: 0 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Section 2: Journal Entry Numbering Format */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "secondary.main" }}>
                      format_list_numbered
                    </Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 2: Journal Entry Numbering Format
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Useful for audit traceability and regional compliance
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Journal ID Prefix"
                      value={settings.journal_id_prefix}
                      onChange={(e) =>
                        handleTextChange("journal_id_prefix", e.target.value)
                      }
                      placeholder="JE-"
                      helperText="Prefix for journal entry IDs (e.g., JE-2024-0001)"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.sequence_reset_annually}
                              onChange={() =>
                                handleToggleChange("sequence_reset_annually")
                              }
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Reset Sequence Annually
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Reset journal sequence each year
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.branch_based_sequence}
                              onChange={() =>
                                handleToggleChange("branch_based_sequence")
                              }
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Branch-Based Sequence
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                JE numbering unique per branch
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<Icon>info</Icon>}>
                      <Typography variant="body2">
                        <strong>Current Format:</strong>{" "}
                        {settings.journal_id_prefix}
                        {settings.sequence_reset_annually ? "YYYY-" : ""}
                        {settings.branch_based_sequence ? "[BRANCH]-" : ""}
                        00001
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Example: {settings.journal_id_prefix}2024
                        {settings.branch_based_sequence ? "-SITRA" : ""}-00123
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Section 3: Posting Permissions & Role Control */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "warning.main" }}>security</Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 3: Posting Permissions & Role Control
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Ensures separation of duties and internal controls
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Roles Allowed to Create Journals</InputLabel>
                      <Select
                        multiple
                        value={settings.roles_allowed_to_create}
                        onChange={(e) =>
                          handleRoleChange(
                            "roles_allowed_to_create",
                            e.target.value as string[]
                          )
                        }
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {(selected as string[]).map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {availableRoles.map((role) => (
                          <MenuItem key={role} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Roles Allowed to Post Journals</InputLabel>
                      <Select
                        multiple
                        value={settings.roles_allowed_to_post}
                        onChange={(e) =>
                          handleRoleChange(
                            "roles_allowed_to_post",
                            e.target.value as string[]
                          )
                        }
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {(selected as string[]).map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {availableRoles.map((role) => (
                          <MenuItem key={role} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Roles Allowed to Reverse Journals</InputLabel>
                      <Select
                        multiple
                        value={settings.roles_allowed_to_reverse}
                        onChange={(e) =>
                          handleRoleChange(
                            "roles_allowed_to_reverse",
                            e.target.value as string[]
                          )
                        }
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {(selected as string[]).map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {availableRoles.map((role) => (
                          <MenuItem key={role} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.require_approval_before_posting}
                              onChange={() =>
                                handleToggleChange(
                                  "require_approval_before_posting"
                                )
                              }
                              color="warning"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Require Approval Before Posting
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Creates two-step flow: Draft → Approval → Post
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Section 4: Audit & Logging Settings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "success.main" }}>audit</Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 4: Audit & Logging Settings
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Ensures financial audit integrity
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  {auditSettings.map((setting) => (
                    <Grid item xs={12} sm={6} md={3} key={setting.key}>
                      <Card variant="outlined" sx={{ height: "100%" }}>
                        <CardContent>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  settings[
                                    setting.key as keyof JournalSettings
                                  ] as boolean
                                }
                                onChange={() =>
                                  handleToggleChange(
                                    setting.key as keyof JournalSettings
                                  )
                                }
                                color={setting.color as any}
                              />
                            }
                            label={
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {setting.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                  }}
                                >
                                  {setting.description}
                                </Typography>
                              </Box>
                            }
                            sx={{ alignItems: "flex-start", m: 0 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Section 5: Validation Rules & Safeguards */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "error.main" }}>rule</Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 5: Validation Rules & Safeguards
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Prevents misuse, enforces clean data
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  {validationRules.map((rule) => (
                    <Grid item xs={12} sm={6} md={3} key={rule.key}>
                      <Card variant="outlined" sx={{ height: "100%" }}>
                        <CardContent>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  settings[
                                    rule.key as keyof JournalSettings
                                  ] as boolean
                                }
                                onChange={() =>
                                  handleToggleChange(
                                    rule.key as keyof JournalSettings
                                  )
                                }
                                color={rule.color as any}
                              />
                            }
                            label={
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {rule.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                  }}
                                >
                                  {rule.description}
                                </Typography>
                              </Box>
                            }
                            sx={{ alignItems: "flex-start", m: 0 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Section 6: Advanced Features */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "info.main" }}>tune</Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 6: Advanced Features (Optional)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Optional features for project/cost center/multi-currency
                      support
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  {advancedFeatures.map((feature) => (
                    <Grid item xs={12} sm={6} md={4} key={feature.key}>
                      <Card variant="outlined" sx={{ height: "100%" }}>
                        <CardContent>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  settings[
                                    feature.key as keyof JournalSettings
                                  ] as boolean
                                }
                                onChange={() =>
                                  handleToggleChange(
                                    feature.key as keyof JournalSettings
                                  )
                                }
                                color={feature.color as any}
                              />
                            }
                            label={
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {feature.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                  }}
                                >
                                  {feature.description}
                                </Typography>
                              </Box>
                            }
                            sx={{ alignItems: "flex-start", m: 0 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Default Currency</InputLabel>
                      <Select
                        value={settings.default_currency}
                        onChange={(e) =>
                          handleTextChange("default_currency", e.target.value)
                        }
                      >
                        {availableCurrencies.map((currency) => (
                          <MenuItem key={currency} value={currency}>
                            {currency}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};
